/**
 * Transaction service layer for business logic and data operations
 * Handles backend integration with proper error handling and offline support
 */
import { api, ApiError } from './api';
import { Transaction, TransactionType, TransactionStatus, TransactionStatusUpdateData, TransactionStatusBulkUpdateData, TransactionStatusCounts } from '../types';

// Local storage keys for offline support
const TRANSACTIONS_CACHE_KEY = 'finstack_transactions_cache';
const CACHE_TIMESTAMP_KEY = 'finstack_transactions_cache_timestamp';
const LAST_SYNC_TIMESTAMP_KEY = 'finstack_transactions_last_sync';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export interface TransactionCreateData {
  userTransactionId: string; // Required for idempotency
  timestamp: Date | string;
  description: string;
  amount: number;
  type: 'income' | 'expense';
  reconciled?: boolean;
  currency: string; // ISO 4217 currency code
  accountId: string;
  accountName: string;
  glAccountNumber: string;
  glAccountName: string;
  glSubcategory?: string;
  category: string;
  ruleBasedAssignment?: boolean;
  appliedRuleId?: string;
  appliedRuleName?: string;
  aiPredictionConfidence?: number;
  aiPredictionUsed?: boolean;
  status?: TransactionStatus;
  reviewedAt?: Date | string;
  reviewedBy?: string;
}

export interface TransactionUpdateData {
  id: string;
  timestamp?: Date | string;
  description?: string;
  amount?: number;
  type?: 'income' | 'expense';
  reconciled?: boolean;
  currency?: string; // ISO 4217 currency code
  accountId?: string;
  accountName?: string;
  glAccountNumber?: string;
  glAccountName?: string;
  glSubcategory?: string;
  category?: string;
  ruleBasedAssignment?: boolean;
  appliedRuleId?: string;
  appliedRuleName?: string;
  aiPredictionConfidence?: number;
  aiPredictionUsed?: boolean;
  status?: TransactionStatus;
  reviewedAt?: Date | string;
  reviewedBy?: string;
}

export interface TransactionFilters {
  accountId?: string;
  category?: string;
  type?: 'income' | 'expense';
  reconciled?: boolean;
  currency?: string; // Filter by currency
  startDate?: Date | string;
  endDate?: Date | string;
  minAmount?: number;
  maxAmount?: number;
  search?: string;
  glAccountNumber?: string;
  status?: TransactionStatus;
  reviewedBy?: string;
}

export interface TransactionPagination {
  limit?: number;
  offset?: number;
}

export interface BulkImportRequest {
  transactions: TransactionCreateData[];
  skipDuplicates?: boolean;
  validateOnly?: boolean;
}

export interface PredictionRequest {
  description: string;
  amount: number;
  type: 'income' | 'expense';
  accountName?: string;
}

export interface BatchPredictionRequest {
  transactions: PredictionRequest[];
}

/**
 * Transaction service with offline support and caching
 */
export class TransactionService {
  // Cache performance metrics
  private cacheHitCount = 0;
  private cacheMissCount = 0;
  private cacheValidationCount = 0;
  private lastValidationTime: Date | null = null;

  /**
   * Get cache performance metrics
   */
  getCacheMetrics() {
    const total = this.cacheHitCount + this.cacheMissCount;
    const hitRate = total > 0 ? (this.cacheHitCount / total) * 100 : 0;
    
    return {
      hitCount: this.cacheHitCount,
      missCount: this.cacheMissCount,
      hitRate: Math.round(hitRate * 100) / 100,
      validationCount: this.cacheValidationCount,
      lastValidationTime: this.lastValidationTime,
      cacheAge: this.getCacheAge()
    };
  }

  /**
   * Get cache age in minutes
   */
  private getCacheAge(): number | null {
    try {
      const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
      if (!timestamp) return null;
      
      const cacheTime = parseInt(timestamp);
      const ageMs = Date.now() - cacheTime;
      return Math.round(ageMs / (1000 * 60)); // Convert to minutes
    } catch {
      return null;
    }
  }

  /**
   * Reset cache performance metrics
   */
  resetCacheMetrics(): void {
    this.cacheHitCount = 0;
    this.cacheMissCount = 0;
    this.cacheValidationCount = 0;
    this.lastValidationTime = null;
  }

  /**
   * Get cached transactions from localStorage
   */
  private getCachedTransactions(): Transaction[] | null {
    try {
      const cached = localStorage.getItem(TRANSACTIONS_CACHE_KEY);
      const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
      
      if (!cached || !timestamp) {
        return null;
      }
      
      const cacheAge = Date.now() - parseInt(timestamp);
      if (cacheAge > CACHE_DURATION) {
        // Cache expired
        this.clearCache();
        return null;
      }
      
      const transactions = JSON.parse(cached) as Transaction[];
      return transactions.map(transaction => ({
        ...transaction,
        date: new Date(transaction.timestamp),
      }));
    } catch (error) {
      console.error('Error reading transactions cache:', error);
      return null;
    }
  }

  /**
   * Cache transactions to localStorage with intelligent size management
   */
  private cacheTransactions(transactions: Transaction[]): void {
    try {
      const serializedTransactions = transactions.map(transaction => ({
        ...transaction,
        date: transaction.timestamp instanceof Date ? transaction.timestamp.toISOString() : transaction.timestamp,
      }));
      
      const dataToCache = JSON.stringify(serializedTransactions);
      
      // Check if cache size is getting too large (>2MB), implement cache pruning
      if (dataToCache.length > 2 * 1024 * 1024) {
        console.warn('Transaction cache size exceeding 2MB, implementing intelligent pruning...');
        this.pruneCacheIfNeeded(serializedTransactions);
      } else {
        localStorage.setItem(TRANSACTIONS_CACHE_KEY, dataToCache);
        localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
        localStorage.setItem(LAST_SYNC_TIMESTAMP_KEY, Date.now().toString());
      }
    } catch (error) {
      if (error instanceof DOMException && error.code === 22) {
        // Quota exceeded, implement emergency cache pruning
        console.warn('LocalStorage quota exceeded, implementing emergency cache pruning...');
        this.emergencyCachePruning();
      } else {
        console.error('Error caching transactions:', error);
      }
    }
  }

  /**
   * Intelligent cache pruning - keep most recent and important transactions
   */
  private pruneCacheIfNeeded(transactions: any[]): void {
    try {
      // Sort by date descending and keep most recent 1000 transactions
      const sortedTransactions = transactions
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, 1000);
      
      const prunedData = JSON.stringify(sortedTransactions);
      localStorage.setItem(TRANSACTIONS_CACHE_KEY, prunedData);
      localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
      localStorage.setItem(LAST_SYNC_TIMESTAMP_KEY, Date.now().toString());
      
      console.log(`Cache pruned: kept ${sortedTransactions.length} most recent transactions`);
    } catch (error) {
      console.error('Cache pruning failed:', error);
      this.clearCache();
    }
  }

  /**
   * Emergency cache pruning when localStorage quota is exceeded
   */
  private emergencyCachePruning(): void {
    try {
      // Clear transaction cache and try to save minimal essential data
      this.clearCache();
      console.log('Emergency cache pruning completed - cache cleared');
    } catch (error) {
      console.error('Emergency cache pruning failed:', error);
    }
  }

  /**
   * Clear transactions cache
   */
  private clearCache(): void {
    localStorage.removeItem(TRANSACTIONS_CACHE_KEY);
    localStorage.removeItem(CACHE_TIMESTAMP_KEY);
    localStorage.removeItem(LAST_SYNC_TIMESTAMP_KEY);
  }

  /**
   * Get the last sync timestamp
   */
  getLastSyncTimestamp(): Date | null {
    try {
      const timestamp = localStorage.getItem(LAST_SYNC_TIMESTAMP_KEY);
      return timestamp ? new Date(parseInt(timestamp)) : null;
    } catch (error) {
      console.error('Error reading last sync timestamp:', error);
      return null;
    }
  }

  /**
   * Get all transactions with caching support
   */
  async getAll(filters?: TransactionFilters, pagination?: TransactionPagination, useCache: boolean = true): Promise<{ transactions: Transaction[]; totalCount: number; hasMore: boolean }> {
    // For filtered requests, we don't use cache
    if (filters || pagination) {
      try {
        const response = await api.transaction.getAll(filters, pagination);
        return response;
      } catch (error) {
        throw error;
      }
    }

    // Try cache first if enabled
    if (useCache) {
      const cached = this.getCachedTransactions();
      if (cached) {
        this.cacheHitCount++;
        return {
          transactions: cached,
          totalCount: cached.length,
          hasMore: false
        };
      } else {
        this.cacheMissCount++;
      }
    } else {
      this.cacheMissCount++;
    }

    try {
      const response = await api.transaction.getAll();
      
      // Cache the results (only for unfiltered requests)
      this.cacheTransactions(response.transactions);
      
      return response;
    } catch (error) {
      // If API fails, try to return cached data as fallback
      const cached = this.getCachedTransactions();
      if (cached) {
        console.warn('API failed, using cached transactions');
        return {
          transactions: cached,
          totalCount: cached.length,
          hasMore: false
        };
      }
      
      throw error;
    }
  }

  /**
   * Get a transaction by ID
   */
  async getById(id: string): Promise<Transaction | null> {
    if (!id) {
      throw new ApiError('Transaction ID is required', 'VALIDATION_ERROR');
    }

    try {
      return await api.transaction.getById(id);
    } catch (error) {
      throw new ApiError('Failed to fetch transaction', 'FETCH_ERROR', error);
    }
  }

  /**
   * Create a new transaction
   */
  async create(transactionData: TransactionCreateData): Promise<Transaction> {
    // Validate required fields
    if (!transactionData.timestamp) {
      throw new ApiError('Transaction timestamp is required', 'VALIDATION_ERROR');
    }
    if (!transactionData.description?.trim()) {
      throw new ApiError('Transaction description is required', 'VALIDATION_ERROR');
    }
    if (transactionData.amount === undefined || transactionData.amount === null) {
      throw new ApiError('Transaction amount is required', 'VALIDATION_ERROR');
    }
    if (!transactionData.type) {
      throw new ApiError('Transaction type is required', 'VALIDATION_ERROR');
    }
    if (!transactionData.accountId || !transactionData.accountName) {
      throw new ApiError('Account information is required', 'VALIDATION_ERROR');
    }
    if (!transactionData.glAccountNumber || !transactionData.glAccountName) {
      throw new ApiError('GL account information is required', 'VALIDATION_ERROR');
    }
    
    // Validate status if provided
    if (transactionData.status && !['UNDER_REVIEW', 'APPROVED', 'DECLINED'].includes(transactionData.status)) {
      throw new ApiError('Invalid transaction status', 'VALIDATION_ERROR');
    }

    try {
      const newTransaction = await api.transaction.create(transactionData);
      
      // Update cache optimistically
      const cached = this.getCachedTransactions();
      if (cached) {
        this.cacheTransactions([...cached, newTransaction]);
      }
      
      return newTransaction;
    } catch (error) {
      if (error instanceof ApiError && error.code === 'VALIDATION_ERROR') {
        throw error;
      }
      throw new ApiError('Failed to create transaction', 'CREATE_ERROR', error);
    }
  }

  /**
   * Update an existing transaction
   */
  async update(updateData: TransactionUpdateData): Promise<Transaction> {
    if (!updateData.id) {
      throw new ApiError('Transaction ID is required', 'VALIDATION_ERROR');
    }

    try {
      const updatedTransaction = await api.transaction.update(updateData);

      // Update cache optimistically
      const cached = this.getCachedTransactions();
      if (cached) {
        const updatedCache = cached.map(transaction =>
          transaction.id === updateData.id ? updatedTransaction : transaction
        );
        this.cacheTransactions(updatedCache);
      }

      return updatedTransaction;
    } catch (error) {
      throw new ApiError('Failed to update transaction', 'UPDATE_ERROR', error);
    }
  }

  /**
   * Delete a transaction with automatic retry for stale data
   */
  async delete(id: string): Promise<void> {
    if (!id) {
      throw new ApiError('Transaction ID is required', 'VALIDATION_ERROR');
    }

    try {
      await api.transaction.delete(id);
      
      // Update cache optimistically
      const cached = this.getCachedTransactions();
      if (cached) {
        const filteredCache = cached.filter(transaction => transaction.id !== id);
        this.cacheTransactions(filteredCache);
      }
    } catch (error: any) {
      // Check if this might be a stale data error
      if (error?.code === 'NOT_FOUND' || 
          error?.message?.includes('not found') || 
          error?.message?.includes('does not exist')) {
        console.warn(`Transaction ${id} not found on backend, refreshing cache...`);
        await this.forceRefresh();
        throw new ApiError('Transaction no longer exists. Data has been refreshed, please try again.', 'STALE_DATA', error);
      }
      
      throw new ApiError('Failed to delete transaction', 'DELETE_ERROR', error);
    }
  }

  /**
   * Update transaction status
   */
  async updateStatus(statusData: TransactionStatusUpdateData): Promise<Transaction> {
    if (!statusData.id) {
      throw new ApiError('Transaction ID is required', 'VALIDATION_ERROR');
    }
    if (!statusData.status) {
      throw new ApiError('Transaction status is required', 'VALIDATION_ERROR');
    }

    try {
      const updatedTransaction = await api.transaction.updateStatus(statusData);
      
      // Update cache optimistically
      const cached = this.getCachedTransactions();
      if (cached) {
        const updatedCache = cached.map(transaction => 
          transaction.id === statusData.id ? updatedTransaction : transaction
        );
        this.cacheTransactions(updatedCache);
      }
      
      return updatedTransaction;
    } catch (error) {
      throw new ApiError('Failed to update transaction status', 'STATUS_UPDATE_ERROR', error);
    }
  }

  /**
   * Bulk update transaction status
   */
  async bulkUpdateStatus(bulkStatusData: TransactionStatusBulkUpdateData): Promise<Transaction[]> {
    if (!bulkStatusData.transactionIds?.length) {
      throw new ApiError('Transaction IDs are required', 'VALIDATION_ERROR');
    }
    if (!bulkStatusData.status) {
      throw new ApiError('Transaction status is required', 'VALIDATION_ERROR');
    }

    try {
      const updatedTransactions = await api.transaction.bulkUpdateStatus(bulkStatusData);
      
      // Update cache optimistically
      const cached = this.getCachedTransactions();
      if (cached) {
        const updatedTransactionMap = new Map(updatedTransactions.map(t => [t.id, t]));
        const updatedCache = cached.map(transaction => 
          updatedTransactionMap.get(transaction.id) || transaction
        );
        this.cacheTransactions(updatedCache);
      }
      
      return updatedTransactions;
    } catch (error) {
      throw new ApiError('Failed to bulk update transaction status', 'BULK_STATUS_UPDATE_ERROR', error);
    }
  }

  /**
   * Approve a transaction
   */
  async approveTransaction(transactionId: string, userId?: string): Promise<Transaction> {
    if (!transactionId) {
      throw new ApiError('Transaction ID is required', 'VALIDATION_ERROR');
    }

    try {
      const updatedTransaction = await api.transaction.approveTransaction(transactionId, userId);
      
      // Update cache optimistically
      const cached = this.getCachedTransactions();
      if (cached) {
        const updatedCache = cached.map(transaction => 
          transaction.id === transactionId ? updatedTransaction : transaction
        );
        this.cacheTransactions(updatedCache);
      }
      
      return updatedTransaction;
    } catch (error) {
      throw new ApiError('Failed to approve transaction', 'APPROVE_ERROR', error);
    }
  }

  /**
   * Decline a transaction
   */
  async declineTransaction(transactionId: string, userId?: string, reason?: string): Promise<Transaction> {
    if (!transactionId) {
      throw new ApiError('Transaction ID is required', 'VALIDATION_ERROR');
    }

    try {
      const updatedTransaction = await api.transaction.declineTransaction(transactionId, userId, reason);
      
      // Update cache optimistically
      const cached = this.getCachedTransactions();
      if (cached) {
        const updatedCache = cached.map(transaction => 
          transaction.id === transactionId ? updatedTransaction : transaction
        );
        this.cacheTransactions(updatedCache);
      }
      
      return updatedTransaction;
    } catch (error) {
      throw new ApiError('Failed to decline transaction', 'DECLINE_ERROR', error);
    }
  }

  /**
   * Get transactions with UNDER_REVIEW status with pagination
   */
  async getReviewTransactions(pagination?: { limit?: number; offset?: number }): Promise<{
    transactions: Transaction[];
    totalCount: number;
    hasMore: boolean;
    limit: number;
    offset: number;
  }> {
    try {
      return await api.transaction.getTransactionsByStatus('UNDER_REVIEW', pagination);
    } catch (error) {
      throw new ApiError('Failed to fetch review transactions', 'FETCH_ERROR', error);
    }
  }

  /**
   * Get transactions with APPROVED status with pagination
   */
  async getApprovedTransactions(pagination?: { limit?: number; offset?: number }): Promise<{
    transactions: Transaction[];
    totalCount: number;
    hasMore: boolean;
    limit: number;
    offset: number;
  }> {
    try {
      return await api.transaction.getTransactionsByStatus('APPROVED', pagination);
    } catch (error) {
      throw new ApiError('Failed to fetch approved transactions', 'FETCH_ERROR', error);
    }
  }

  /**
   * Get transactions with DECLINED status with pagination
   */
  async getDeclinedTransactions(pagination?: { limit?: number; offset?: number }): Promise<{
    transactions: Transaction[];
    totalCount: number;
    hasMore: boolean;
    limit: number;
    offset: number;
  }> {
    try {
      return await api.transaction.getTransactionsByStatus('DECLINED', pagination);
    } catch (error) {
      throw new ApiError('Failed to fetch declined transactions', 'FETCH_ERROR', error);
    }
  }

  /**
   * Get transactions by status
   */
  async getTransactionsByStatus(
    status: string, 
    pagination?: { limit?: number; offset?: number }
  ): Promise<{
    transactions: Transaction[];
    totalCount: number;
    hasMore: boolean;
    limit: number;
    offset: number;
  }> {
    try {
      return await api.transaction.getTransactionsByStatus(status, pagination);
    } catch (error) {
      throw new ApiError(`Failed to fetch transactions with status: ${status}`, 'FETCH_ERROR', error);
    }
  }

  /**
   * Get transaction status counts
   */
  async getStatusCounts(): Promise<TransactionStatusCounts> {
    try {
      return await api.transaction.getTransactionStatusCounts();
    } catch (error) {
      throw new ApiError('Failed to fetch transaction status counts', 'STATUS_COUNTS_ERROR', error);
    }
  }

  /**
   * Bulk import transactions
   */
  async bulkImport(importRequest: BulkImportRequest): Promise<any> {
    if (!importRequest.transactions?.length) {
      throw new ApiError('No transactions provided for import', 'VALIDATION_ERROR');
    }

    // Validate all transactions
    for (const transaction of importRequest.transactions) {
      if (!transaction.timestamp) {
        throw new ApiError('Transaction timestamp is required', 'VALIDATION_ERROR');
      }
      
      if (!transaction.description || !transaction.description.trim()) {
        throw new ApiError('Transaction description is required and cannot be empty', 'VALIDATION_ERROR');
      }
      
      if (transaction.amount === undefined || transaction.amount === null) {
        throw new ApiError('Transaction amount is required', 'VALIDATION_ERROR');
      }
      
      if (!transaction.type) {
        throw new ApiError('Transaction type (income/expense) is required', 'VALIDATION_ERROR');
      }
      
      if (!transaction.accountId) {
        throw new ApiError('Account ID is required', 'VALIDATION_ERROR');
      }
      
      if (!transaction.currency) {
        throw new ApiError('Currency is required', 'VALIDATION_ERROR');
      }
      
      if (!transaction.userTransactionId) {
        throw new ApiError('User Transaction ID is required', 'VALIDATION_ERROR');
      }
    }

    try {
      const result = await api.transaction.bulkImport(importRequest);
      
      // Force refresh after bulk operations to ensure cache consistency
      if (result.successful && result.successful.length > 0) {
        console.log(`Bulk import completed: ${result.successCount} transactions imported. Refreshing cache...`);
        await this.forceRefresh();
      }
      
      return result;
    } catch (error) {
      throw new ApiError('Failed to import transactions', 'BULK_IMPORT_ERROR', error);
    }
  }

  /**
   * Bulk delete transactions
   */
  async deleteBulk(ids: string[]): Promise<any> {
    if (!ids.length) {
      throw new ApiError('No transaction IDs provided', 'VALIDATION_ERROR');
    }

    try {
      const result = await api.transaction.bulkDelete(ids);
      
      // Update cache optimistically
      const cached = this.getCachedTransactions();
      if (cached) {
        const idSet = new Set(ids);
        const filteredCache = cached.filter(transaction => !idSet.has(transaction.id));
        this.cacheTransactions(filteredCache);
      }
      
      return result;
    } catch (error) {
      throw new ApiError('Failed to delete transactions', 'BULK_DELETE_ERROR', error);
    }
  }


  /**
   * Predict GL account for a single transaction
   */
  async predictGLAccount(predictionData: PredictionRequest): Promise<any> {
    console.log("🔮 [TransactionService] Starting single GL account prediction:", predictionData);
    
    if (!predictionData.description?.trim()) {
      console.error("🔮 [TransactionService] Validation error: Transaction description is required");
      throw new ApiError('Transaction description is required for prediction', 'VALIDATION_ERROR');
    }

    try {
      console.log("🔮 [TransactionService] Calling API layer prediction service");
      const result = await api.transaction.predictGLAccount(predictionData);
      console.log("🔮 [TransactionService] API prediction result:", result);
      return result;
    } catch (error) {
      console.error("🔮 [TransactionService] Prediction error:", error);
      console.error("🔮 [TransactionService] Error details:", {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        predictionData
      });
      throw new ApiError('Failed to predict GL account', 'PREDICTION_ERROR', error);
    }
  }

  /**
   * Batch predict GL accounts for multiple transactions
   */
  async batchPredictGLAccounts(batchRequest: BatchPredictionRequest): Promise<any> {
    console.log("🔮 [TransactionService] Starting batch GL account prediction:", {
      transactionCount: batchRequest.transactions?.length || 0,
      transactions: batchRequest.transactions?.map(t => ({ description: t.description, amount: t.amount, type: t.type }))
    });
    
    if (!batchRequest.transactions?.length) {
      console.error("🔮 [TransactionService] Validation error: No transactions provided for prediction");
      throw new ApiError('No transactions provided for prediction', 'VALIDATION_ERROR');
    }

    try {
      console.log("🔮 [TransactionService] Calling API layer batch prediction service");
      const result = await api.transaction.batchPredictGLAccounts(batchRequest);
      console.log("🔮 [TransactionService] API batch prediction result:", result);
      return result;
    } catch (error) {
      console.error("🔮 [TransactionService] Batch prediction error:", error);
      console.error("🔮 [TransactionService] Batch error details:", {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        batchRequest: {
          transactionCount: batchRequest.transactions?.length,
          firstTransaction: batchRequest.transactions?.[0]
        }
      });
      throw new ApiError('Failed to batch predict GL accounts', 'BATCH_PREDICTION_ERROR', error);
    }
  }

  /**
   * Search transactions by term
   */
  async search(searchTerm: string): Promise<Transaction[]> {
    const response = await this.getAll({ search: searchTerm });
    return response.transactions;
  }

  /**
   * Get transactions by GL account
   */
  async getByGLAccount(glAccountNumber: string): Promise<Transaction[]> {
    const response = await this.getAll({ glAccountNumber });
    return response.transactions;
  }

  /**
   * Get transactions by account
   */
  async getByAccount(accountId: string): Promise<Transaction[]> {
    const response = await this.getAll({ accountId });
    return response.transactions;
  }

  /**
   * Get transactions by status (shorthand method)
   */
  async getByStatus(status: string): Promise<Transaction[]> {
    const response = await this.getAll({ status: status as any });
    return response.transactions;
  }

  /**
   * Refresh cache by forcing a new fetch
   */
  async refresh(): Promise<Transaction[]> {
    this.clearCache();
    const response = await this.getAll(undefined, undefined, false);
    return response.transactions;
  }

  /**
   * Force refresh cache (alias for refresh for clarity)
   */
  async forceRefresh(): Promise<Transaction[]> {
    return this.refresh();
  }

  /**
   * Validate cache against backend and detect inconsistencies
   */
  async validateCache(): Promise<{ isValid: boolean; issues: string[] }> {
    this.cacheValidationCount++;
    this.lastValidationTime = new Date();
    const issues: string[] = [];
    
    try {
      const cached = this.getCachedTransactions();
      const fresh = await api.transaction.getAll();
      
      if (!cached) {
        return { isValid: true, issues: [] }; // No cache to validate
      }
      
      // Check count mismatch
      if (cached.length !== fresh.transactions.length) {
        issues.push(`Transaction count mismatch: cache has ${cached.length}, backend has ${fresh.transactions.length}`);
      }
      
      // Check for transactions in cache but not in backend (phantom transactions)
      const cachedIds = new Set(cached.map(t => t.id));
      const freshIds = new Set(fresh.transactions.map(t => t.id));
      
      const phantomTransactions = cached.filter(t => !freshIds.has(t.id));
      if (phantomTransactions.length > 0) {
        issues.push(`${phantomTransactions.length} phantom transactions found in cache`);
      }
      
      // Check for transactions in backend but not in cache (missing transactions)
      const missingTransactions = fresh.transactions.filter(t => !cachedIds.has(t.id));
      if (missingTransactions.length > 0) {
        issues.push(`${missingTransactions.length} transactions missing from cache`);
      }
      
      return {
        isValid: issues.length === 0,
        issues
      };
    } catch (error) {
      console.error('Cache validation failed:', error);
      return {
        isValid: false,
        issues: ['Cache validation failed due to network error']
      };
    }
  }

  /**
   * Auto-refresh cache if validation detects issues
   */
  async validateAndRefresh(): Promise<Transaction[]> {
    const validation = await this.validateCache();
    
    if (!validation.isValid) {
      console.warn('Cache validation failed:', validation.issues);
      console.log('Auto-refreshing cache...');
      return this.forceRefresh();
    }
    
    const response = await this.getAll();
    return response.transactions;
  }

  /**
   * Intelligent cache warming - preload data when cache is stale but not expired
   */
  async warmCache(): Promise<void> {
    try {
      const cacheAge = this.getCacheAge();
      const cached = this.getCachedTransactions();
      
      // If cache is more than 3 minutes old but not expired (5 minutes), warm it up
      if (cacheAge && cacheAge > 3 && cached) {
        console.debug('Warming cache in background...');
        // Preload in background without blocking current operations
        setTimeout(async () => {
          try {
            await this.getAll(undefined, undefined, false);
            console.debug('Cache warmed successfully');
          } catch (error) {
            console.debug('Cache warming failed:', error);
          }
        }, 0);
      }
    } catch (error) {
      console.debug('Cache warming evaluation failed:', error);
    }
  }

  /**
   * Check cache health and provide detailed status
   */
  getCacheHealth(): {
    status: 'healthy' | 'stale' | 'expired' | 'missing';
    age: number | null;
    details: string;
  } {
    const cached = localStorage.getItem(TRANSACTIONS_CACHE_KEY);
    const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    
    if (!cached || !timestamp) {
      return {
        status: 'missing',
        age: null,
        details: 'No cache data found'
      };
    }
    
    const cacheAge = this.getCacheAge();
    if (!cacheAge) {
      return {
        status: 'missing',
        age: null,
        details: 'Invalid cache timestamp'
      };
    }
    
    if (cacheAge > 5) {
      return {
        status: 'expired',
        age: cacheAge,
        details: `Cache expired ${cacheAge} minutes ago`
      };
    }
    
    if (cacheAge > 3) {
      return {
        status: 'stale',
        age: cacheAge,
        details: `Cache is ${cacheAge} minutes old (warming recommended)`
      };
    }
    
    return {
      status: 'healthy',
      age: cacheAge,
      details: `Cache is fresh (${cacheAge} minutes old)`
    };
  }

  /**
   * Split a transaction into multiple child transactions
   */
  async splitTransaction(request: { transactionId: string; splits: any[] }): Promise<any> {
    if (!request.transactionId) {
      throw new ApiError('Transaction ID is required', 'VALIDATION_ERROR');
    }
    if (!request.splits || !Array.isArray(request.splits) || request.splits.length < 2) {
      throw new ApiError('At least 2 splits are required', 'VALIDATION_ERROR');
    }

    // Validate each split
    for (const split of request.splits) {
      if (!split.description?.trim()) {
        throw new ApiError('Split description is required', 'VALIDATION_ERROR');
      }
      if (!split.amount || split.amount <= 0) {
        throw new ApiError('Split amount must be greater than 0', 'VALIDATION_ERROR');
      }
      if (!split.glAccountNumber) {
        throw new ApiError('Split GL account is required', 'VALIDATION_ERROR');
      }
    }

    try {
      const result = await api.transaction.splitTransaction(request);
      
      // Force refresh cache after split operation to ensure consistency
      console.log('Split transaction completed. Refreshing cache...');
      await this.forceRefresh();
      
      return result;
    } catch (error) {
      throw new ApiError('Failed to split transaction', 'SPLIT_ERROR', error);
    }
  }

  /**
   * Get child transactions for a parent transaction
   */
  async getChildTransactions(parentId: string): Promise<Transaction[]> {
    if (!parentId) {
      throw new ApiError('Parent transaction ID is required', 'VALIDATION_ERROR');
    }

    try {
      return await api.transaction.getChildTransactions(parentId);
    } catch (error) {
      throw new ApiError('Failed to fetch child transactions', 'FETCH_ERROR', error);
    }
  }
}

// Export singleton instance
export const transactionService = new TransactionService();
export default transactionService;